import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/utils/format';
import type { AdminLog } from '@/mocks/adminLogs';

type LogsTabProps = {
  logs: AdminLog[];
};

const LEVEL_STYLES: Record<AdminLog['level'], string> = {
  info: 'bg-primary-100 text-primary-900',
  warning: 'bg-accent-100 text-accent-900',
  danger: 'bg-foreground-100 text-foreground-700',
};

const LEVEL_ICONS: Record<AdminLog['level'], string> = {
  info: 'ri-information-line',
  warning: 'ri-error-warning-line',
  danger: 'ri-alert-line',
};

export default function LogsTab({ logs }: LogsTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return logs;
    return logs.filter(
      (log) =>
        log.action.toLowerCase().includes(query) ||
        log.user.toLowerCase().includes(query) ||
        log.ip.toLowerCase().includes(query) ||
        (log.userAgent && log.userAgent.toLowerCase().includes(query)) ||
        (log.reference && log.reference.toLowerCase().includes(query)),
    );
  }, [logs, search]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          {t('admin.logs.title')}
        </h1>
        <p className="mt-1 text-sm text-foreground-600">{t('admin.logs.subtitle')}</p>
      </div>

      <div className="relative">
        <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('admin.logs.searchPh')}
          className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-5 py-3 font-semibold">{t('admin.logs.time')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.logs.ip')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.logs.user')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.logs.action')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.logs.reference')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.logs.technical')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-foreground-500">
                    {t('admin.logs.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map((log) => (
                  <tr key={log.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="whitespace-nowrap px-5 py-3 text-foreground-600">
                      {formatDateTime(log.timestamp)}
                    </td>
                    <td className="px-5 py-3">
                      <span className="font-mono text-xs text-foreground-600">{log.ip}</span>
                    </td>
                    <td className="px-5 py-3 text-foreground-700">{log.user}</td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${LEVEL_STYLES[log.level]}`}
                      >
                        <i className={LEVEL_ICONS[log.level]}></i>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-foreground-600">{log.reference ?? '—'}</td>
                    <td className="max-w-[220px] px-5 py-3">
                      {log.userAgent ? (
                        <span className="block truncate font-mono text-xs text-foreground-500" title={log.userAgent}>
                          {log.userAgent}
                        </span>
                      ) : (
                        <span className="text-xs text-foreground-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}